import { calculateRetention } from "./retentionCalculations";

// --- THE IRON GATE: STRICT DEDUPLICATION ---
const getStrictDedupKey = (p) => {
  const polizza = String(p.codicePolizza || "").trim();
  const data = String(p.dataTransazione || "").trim();
  const premio = Number(p.premioNetto || 0).toFixed(2);
  return `${polizza}|${data}|${premio}`;
};

const forceDedup = (rows) => {
  if (!Array.isArray(rows)) return [];
  const seen = new Set();
  const cleanRows = [];
  rows.forEach((row) => {
    const key = getStrictDedupKey(row);
    if (!seen.has(key)) {
      seen.add(key);
      cleanRows.push(row);
    }
  });
  return cleanRows;
};
// ------------------------------------------

export const isRetentionRelevant = (causale) => {
  return (causale || "").trim().toUpperCase() === "POLIZZA IN SCADENZA";
};

export const calculateKPIs = (rawProduction, expiries, settings) => {
  // 1. DEDUPLICAZIONE
  const production = forceDedup(rawProduction);

  // 2. RETENTION CALCULATION (NEW ENGINE)
  // We calculate retention using the new rigorous engine
  // Note: We use the production provided to this function.
  // If the dashboard filters production, retention will be calculated against that subset.
  const { enrichedExpiries } = calculateRetention(
    production,
    expiries,
    settings,
  );

  const agents = Array.from(
    new Set(production.map((p) => p.collaboratore)),
  ).filter(Boolean);
  // Also include agents from expiries if they are not in production, to show them in leaderboard
  const expiryAgents = Array.from(
    new Set(expiries.map((e) => e.collaboratore)),
  ).filter(Boolean);
  const allAgents = Array.from(new Set([...agents, ...expiryAgents])).sort();

  // DEFINIZIONE FILTRI "IRON GATE" (Identici alla Dashboard)
  const ALLOWED_PRODUCTS = ["AUTO", "MOTO", "VAN"];
  const ALLOWED_ACTIONS = [
    "EMISSIONE ANNUALE",
    "EMISSIONE MENSILE",
    "EMISSIONE SEMESTRALE",
  ];

  return allAgents.map((agent) => {
    const agentProd = production.filter((p) => p.collaboratore === agent);

    // --- CALCOLO FINANZIARIO (Bonus Sconto & Netto) ---
    // Mantiene logica più ampia per il calcolo provvigionale (non filtrata Iron Gate)
    const financialTotals = agentProd.reduce(
      (acc, p) => {
        const prodName = (p.prodotto || "").trim().toLowerCase();
        const rawAction = p.tipoAzione || p.azione || "";
        const actionType = String(rawAction).trim().toLowerCase();
        const policyCode = (p.codicePolizza || "").trim().toUpperCase();

        const motorList = ["auto", "moto", "van", "ciclomotore"];
        const isMotor = motorList.includes(prodName);
        const numGaranzie = Number(p.numeroGaranzie || 0);
        const premioNetto = Number(p.premioNetto || 0);

        // A. COMMISSIONE BASE
        let commRate = 0;
        if (!isMotor) commRate = settings?.commissions?.nonMotorRate ?? 0.25;
        else {
          if (numGaranzie === 0)
            commRate = settings?.commissions?.motor0Gar ?? 0.04;
          else if (numGaranzie === 1)
            commRate = settings?.commissions?.motor1Gar ?? 0.05;
          else commRate = settings?.commissions?.motor2PlusGar ?? 0.065;
        }
        const commValue = premioNetto * commRate;

        // B. BONUS SCONTO
        let bonusVal = 0;
        const isPRP = policyCode.startsWith("PRP");
        const isValidAction =
          actionType.includes("emissione semestrale") ||
          actionType.includes("emissione annuale");

        if (!isPRP && isValidAction) {
          if (isMotor && numGaranzie >= 3) {
            bonusVal = settings?.commissions?.bonusSconto3GarFlat ?? 10;
          } else if (
            !isMotor &&
            (prodName.includes("famiglia - infortuni famiglia") ||
              prodName.includes("casa e famiglia"))
          ) {
            const rate = settings?.commissions?.bonusScontoNonMotorRate ?? 0.5;
            bonusVal = premioNetto * rate;
          }
        }

        return {
          baseCommission: acc.baseCommission + commValue,
          totalBonusSconto: acc.totalBonusSconto + bonusVal,
          nettoMotor: acc.nettoMotor + (isMotor ? premioNetto : 0),
          nettoNonMotor: acc.nettoNonMotor + (!isMotor ? premioNetto : 0),
        };
      },
      {
        baseCommission: 0,
        totalBonusSconto: 0,
        nettoMotor: 0,
        nettoNonMotor: 0,
      },
    );

    // --- CALCOLO KPI STATISTICI ("IRON GATE") ---
    // Qui applichiamo rigorosamente i filtri richiesti per le medie garanzie

    // 1. Filtro: Prodotti Ammessi + No Asterischi + Azioni Ammesse
    const ironSubset = agentProd.filter((p) => {
      const prod = (p.prodotto || "").trim().toUpperCase();
      const code = String(p.codicePolizza || "");
      const action = (p.tipoAzione || "").trim().toUpperCase();

      const isProdOk = ALLOWED_PRODUCTS.includes(prod);
      const isCodeOk = !code.includes("*");
      const isActionOk = ALLOWED_ACTIONS.includes(action);

      return isProdOk && isCodeOk && isActionOk;
    });

    // 2. Suddivisione Nuove (Rischio) vs Rinnovi
    const subsetNew = ironSubset.filter((p) => {
      const r = (p.rischio || "").trim().toUpperCase();
      return r === "RISCHIO" || r === "NUOVA POLIZZA";
    });
    const subsetRen = ironSubset.filter(
      (p) => (p.rischio || "").trim().toUpperCase() === "RINNOVO",
    );

    // 3. Calcolo Medie
    const calcAvg = (rows) => {
      if (rows.length === 0) return 0;
      const total = rows.reduce(
        (sum, r) => sum + (Number(r.numeroGaranzie) || 0),
        0,
      );
      return total / rows.length;
    };

    const avgGarMotorTotal = calcAvg(ironSubset);
    const avgGarMotorNew = calcAvg(subsetNew);
    const avgGarMotorRen = calcAvg(subsetRen);

    // 4. Conteggi Qualità (0G, 1G, 2+G) - Basati sul subset "Iron Gate" per coerenza
    const count0Gar = ironSubset.filter(
      (p) => Number(p.numeroGaranzie) === 0,
    ).length;
    const count1Gar = ironSubset.filter(
      (p) => Number(p.numeroGaranzie) === 1,
    ).length;
    const count2PlusGar = ironSubset.filter(
      (p) => Number(p.numeroGaranzie) >= 2,
    ).length;

    // 5. Variabili Finanziarie e KPI Generali
    const polizzeStrict = agentProd.length; // Totale righe raw per l'agente
    const nettoMotor = financialTotals.nettoMotor;
    const nettoNonMotor = financialTotals.nettoNonMotor;
    const totalVolume = nettoMotor + nettoNonMotor;
    const mixNonMotor =
      totalVolume > 0 ? (nettoNonMotor / totalVolume) * 100 : 0;

    // 6. RETENTION RATE (FROM NEW ENGINE)
    const agentExpiries = enrichedExpiries.filter(
      (e) =>
        e.collaboratore === agent &&
        e.isEligible &&
        e.retentionStatus !== "DUPLICATE",
    );
    const retainedCount = agentExpiries.filter(
      (e) => e.retentionStatus === "RENEWED",
    ).length;
    const retentionRate = agentExpiries.length
      ? (retainedCount / agentExpiries.length) * 100
      : 0;

    // 7. Targets
    const tMix = settings?.targets?.mixNonMotor ?? 1.3;
    const tGarNew = settings?.targets?.garMotorNew ?? 2.0;
    const tRetBase = settings?.targets?.retentionBase ?? 70;
    const tRetSuper = settings?.targets?.retentionSuper ?? 80;

    const reqCross = mixNonMotor >= tMix;
    const reqGar = avgGarMotorNew >= tGarNew;
    const reqRetBase = retentionRate >= tRetBase;
    const reqRetSuper = retentionRate >= tRetSuper;

    // 8. Payout
    const baseCommission = financialTotals.baseCommission;
    const totalBonusSconto = financialTotals.totalBonusSconto;

    let bonusLevel = 0;
    if (reqRetSuper) bonusLevel = 2;
    else if (reqRetBase) bonusLevel = 1;

    const bL1 = settings?.bonusPerc?.level1 ?? 0.01;
    const bL2 = settings?.bonusPerc?.level2 ?? 0.02;
    const bonusPerc = bonusLevel === 2 ? bL2 : bonusLevel === 1 ? bL1 : 0;

    const bonusAmount = totalVolume * bonusPerc;
    const totalPayout = baseCommission + totalBonusSconto + bonusAmount;

    return {
      name: agent,
      nettoMotor,
      nettoNonMotor,
      polizzeStrict,
      totGaranzieStrict: ironSubset.reduce(
        (acc, p) => acc + (Number(p.numeroGaranzie) || 0),
        0,
      ),
      nuoveCount: subsetNew.length,
      nuovePerc: 0,
      rinnoviCount: subsetRen.length,
      rinnoviPerc: 0,
      mixNonMotor,
      avgGarMotorTotal,
      avgGarMotorNew,
      avgGarMotorRen,
      retentionRate,
      baseCommission,
      bonusAmount,
      totalPayout,
      bonusLevel,
      count0Gar,
      count1Gar,
      count2PlusGar,
      reqCross,
      reqGar,
      reqRetBase,
      reqRetSuper,
      totalBonusSconto,
    };
  });
};

export default calculateKPIs;
